/**
 * Error Message Mapping
 * Maps error codes to user-friendly messages
 * @module utils/errorMessages
 */

/**
 * Error message mappings for different error types
 */
export const ERROR_MESSAGES = {
  // Network errors
  NETWORK_ERROR: 'خطأ في الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.',
  TIMEOUT_ERROR: 'انتهت مهلة الاتصال. يرجى المحاولة مرة أخرى.',
  
  // Authentication errors
  UNAUTHORIZED: 'غير مصرح لك بالوصول. يرجى تسجيل الدخول مرة أخرى.',
  FORBIDDEN: 'ليس لديك صلاحية للوصول إلى هذا المورد.',
  TOKEN_EXPIRED: 'انتهت صلاحية جلسة العمل. يرجى تسجيل الدخول مرة أخرى.',
  
  // Validation errors
  VALIDATION_ERROR: 'البيانات المدخلة غير صحيحة. يرجى التحقق من البيانات.',
  INVALID_ID: 'معرف غير صالح. يرجى التحقق من البيانات.',
  
  // Server errors
  SERVER_ERROR: 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقًا.',
  NOT_FOUND: 'المورد المطلوب غير موجود.',
  
  // Generic
  UNKNOWN_ERROR: 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.',
  EMPTY_RESPONSE: 'البيانات غير متوفرة حالياً.'
};

/**
 * Get user-friendly error message
 * @param {Error|Object} error - Error object
 * @param {string} defaultMessage - Default message if no mapping found
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error, defaultMessage = ERROR_MESSAGES.UNKNOWN_ERROR) => {
  // Handle string errors
  if (typeof error === 'string') {
    return ERROR_MESSAGES[error] || error || defaultMessage;
  }

  // Handle axios errors
  if (error?.response) {
    const status = error.response.status;
    const errorCode = error.response.data?.error;
    const message = error.response.data?.message;

    // Check for specific error codes
    if (errorCode && ERROR_MESSAGES[errorCode]) {
      return ERROR_MESSAGES[errorCode];
    }

    // Map HTTP status codes
    switch (status) {
      case 400:
        return message || ERROR_MESSAGES.VALIDATION_ERROR;
      case 401:
        return ERROR_MESSAGES.UNAUTHORIZED;
      case 403:
        return ERROR_MESSAGES.FORBIDDEN;
      case 404:
        return message || ERROR_MESSAGES.NOT_FOUND;
      case 408:
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      case 500:
      case 502:
      case 503:
        return ERROR_MESSAGES.SERVER_ERROR;
      default:
        return message || defaultMessage;
    }
  }

  // Handle network errors
  if (error?.message) {
    if (error.message.includes('Network Error') || error.message.includes('network')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (error.message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }
    return error.message;
  }

  return defaultMessage;
};

/**
 * Check if error is a network error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  if (!error) return false;
  if (error?.message?.includes('Network Error')) return true;
  if (error?.code === 'NETWORK_ERROR') return true;
  return false;
};

/**
 * Check if error is a timeout error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isTimeoutError = (error) => {
  if (!error) return false;
  if (error?.message?.includes('timeout')) return true;
  if (error?.code === 'ECONNABORTED') return true;
  return false;
};

/**
 * Check if error is an authentication error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  if (!error) return false;
  if (error?.response?.status === 401) return true;
  if (error?.response?.status === 403) return true;
  return false;
};

